import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Offcanvas,
  Button,
  Form,
  InputGroup,
  Badge,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTimes,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { ProdutoPromo } from "data/promocoes";
import InfiniteLoader from "react-window-infinite-loader";
import { useInfiniteQuery } from "@tanstack/react-query";
import { FixedSizeList as List } from "react-window";

// Estendendo ProdutoPromo com propriedades para UI
export interface ItemPromocao extends ProdutoPromo {
  codigo?: string;
  descricao: string; // Necessário para exibição
}

interface SelecaoItensOffcanvasProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (items: ItemPromocao[]) => void;
  selectedItems: ItemPromocao[];
  aplicarPara: "PRODUTO" | "DEPARTAMENTO" | "CATEGORIA";
}

const SelecaoItensOffcanvas = ({
  show,
  onHide,
  onConfirm,
  selectedItems,
  aplicarPara,
}: SelecaoItensOffcanvasProps) => {
  const [termoBusca, setTermoBusca] = useState("");
  const [localSelectedItems, setLocalSelectedItems] = useState<
    Map<string, ItemPromocao>
  >(new Map());
  const itemCountRef = useRef<HTMLSpanElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);

  // Estado adicional para rastrear se o componente foi inicializado
  const [isInitialized, setIsInitialized] = useState(false);

  // Helper para identificar o tipo do item baseado nos IDs
  const getItemType = (
    item: ProdutoPromo
  ): "produto" | "departamento" | "categoria" => {
    if ((item.produtoId ?? 0) > 0) return "produto";
    if ((item.departamentoId ?? 0) > 0) return "departamento";
    if ((item.categoriaId ?? 0) > 0) return "categoria";
    return "produto"; // Fallback
  };

  // Helper para obter o ID específico do item baseado no tipo
  const getItemId = (item: ProdutoPromo): number => {
    const tipo = getItemType(item);
    if (tipo === "produto") return item.produtoId ?? 0;
    if (tipo === "departamento") return item.departamentoId ?? 0;
    return item.categoriaId ?? 0;
  };

  // Verifica se um item está selecionado
  const isItemSelected = (item: ItemPromocao) => {
    const tipo = getItemType(item);
    const id = getItemId(item);
    const key = `${tipo}-${id}`;
    return localSelectedItems.has(key);
  };

  // Alterna a seleção de um item
  const toggleItemSelection = (item: ItemPromocao) => {
    const tipo = getItemType(item);
    const id = getItemId(item);
    const key = `${tipo}-${id}`;
    const newLocalItems = new Map(localSelectedItems);

    if (newLocalItems.has(key)) {
      newLocalItems.delete(key);
    } else {
      newLocalItems.set(key, item);
    }

    setLocalSelectedItems(newLocalItems);

    // Atualiza a contagem imediatamente
    if (itemCountRef.current) {
      itemCountRef.current.innerText = `${newLocalItems.size} ${
        newLocalItems.size === 1 ? "item selecionado" : "itens selecionados"
      }`;
    }

    // Aplica as mudanças ao componente pai
    onConfirm(Array.from(newLocalItems.values()));
  };

  // Inicializa a seleção local com os itens selecionados
  useEffect(() => {
    if (show) {
      const map = new Map<string, ItemPromocao>();
      selectedItems.forEach((item) => {
        const tipo = getItemType(item);
        const id = getItemId(item);
        if (id > 0) {
          const key = `${tipo}-${id}`;
          map.set(key, item);
        }
      });
      setLocalSelectedItems(map);
    }
  }, [selectedItems, show]);

  // Função para buscar itens otimizada para useInfiniteQuery
  const buscarItens = async ({ pageParam = 0 }) => {
  
    try {
      if (aplicarPara === "PRODUTO") {
        const { data } = await axios.get("/api/promocao/produtos", {
          params: {
            pageSize: 20,
            pageIndex: pageParam,
            filter: termoBusca || undefined,
          },
        });

        const currentPage = Number(data.pageIndex);
        const totalPages = Number(data.totalPages);
        const nextPage = currentPage < totalPages - 1 ? currentPage + 1 : undefined;

        return {
          itens:
            data.itens?.map((item: any) => ({
              produtoId: item.produtoId,             
              codigo: item.codigo,
              descricao: item.descricao,
            })) || [],
          nextPage,
          pageIndex: currentPage,
          totalPages,
        };
      } else if (aplicarPara === "DEPARTAMENTO") {
        const { data } = await axios.get("/api/promocao/departamentos", {
          params: termoBusca ? { filter: termoBusca } : {},
        });

        console.log("Departamentos response:", data);
        
        // Handle Result pattern: data.data contains the actual departments array
        const items = data && data.data ? 
          (Array.isArray(data.data) ? data.data : []) : 
          (Array.isArray(data) ? data : []);

        return {
          itens: items.map((item: any) => ({
            departamentoId: item.departamentoId,          
            codigo: item.codigo || "",
            descricao: item.descricao,
          })),
          nextPage: undefined, // Departamentos não têm paginação
        };
      } else {
        // CATEGORIA
        const { data } = await axios.get("/api/promocao/categorias", {
          params: termoBusca ? { filter: termoBusca } : {},
        });

        console.log("Categorias response:", data);

        // Handle Result pattern and different response formats
        const items = data && data.data ? 
          (Array.isArray(data.data) ? data.data : []) : 
          (Array.isArray(data) ? data : 
            (Array.isArray(data?.itens) ? data.itens : []));

        return {
          itens: items.map((item: any) => ({
            categoriaId: item.categoriaId,           
            codigo: item.codigo || "",
            descricao: item.descricao,
          })),
          nextPage: undefined, // Categorias não têm paginação
        };
      }
    } catch (error) {
      console.error(`Erro ao buscar ${aplicarPara.toLowerCase()}:`, error);
      
      let errorMsg = "Erro ao carregar itens";
      if (axios.isAxiosError(error) && error.response) {
        // Check for Result pattern error
        if (error.response.data && !error.response.data.isSuccess) {
          errorMsg = error.response.data.message || errorMsg;
        } else {
          errorMsg = `Erro ${error.response.status}: ${error.message}`;
        }
      }
      
    
      return { itens: [], nextPage: undefined };
    }
  };

  // Usando useInfiniteQuery
  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ["itensPromo", aplicarPara, termoBusca],
    queryFn: buscarItens,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
    enabled: show && (isInitialized || termoBusca.length >= 2 || termoBusca.length === 0),
    staleTime: 60000, // 1 minuto
    refetchOnWindowFocus: false,
  });

  // Inicializa a busca quando o offcanvas é exibido
  useEffect(() => {
    if (show && !isInitialized) {
      setIsInitialized(true);
    }

    // Reset quando o componente é fechado
    if (!show && isInitialized) {
      setIsInitialized(false);
    }
  }, [show, isInitialized]);

  // Reset ao abrir o modal ou mudar aplicarPara
  useEffect(() => {
    if (show) {
      setTermoBusca("");
      refetch();
      if (listContainerRef.current) {
        listContainerRef.current.scrollTop = 0;
      }
    }
  }, [show, aplicarPara, refetch]);

  // Busca com debounce quando o termo muda
  useEffect(() => {
    if (!show) return;

    const timer = setTimeout(() => {
      if (termoBusca.length >= 2 || termoBusca.length === 0) {
        refetch().catch(error => {
          console.error(`Erro ao buscar ${aplicarPara.toLowerCase()}:`, error);
        });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [termoBusca, show, refetch, aplicarPara]);

  // Preparando todos os itens para renderização
  const allItems = useMemo(() => {
    return data?.pages.flatMap((page) => page.itens) || [];
  }, [data]);

  // Funções para o InfiniteLoader
  const itemCount = hasNextPage ? allItems.length + 1 : allItems.length;

  const isItemLoaded = (index: number) => {
    return !hasNextPage || index < allItems.length;
  };

  const loadMoreItems = async (startIndex: number, stopIndex: number): Promise<void> => {
    if (isFetchingNextPage) return;
    await fetchNextPage();
  };

  // Renderizador de linha para a lista virtualizada
  const ItemRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    // Se estamos carregando o último item
    if (!isItemLoaded(index)) {
      return (
        <div style={style} className="p-3 text-center">
          <Spinner animation="border" size="sm" variant="primary" />
        </div>
      );
    }

    const item = allItems[index];
    const tipo = getItemType(item);
    const id = getItemId(item);
    const key = `${tipo}-${id}`;
    const selected = isItemSelected(item);

    return (
      <div
        key={key}
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          borderBottom: "1px solid #dee2e6",
          padding: "12px 24px",
        }}
        onClick={() => toggleItemSelection(item)}
        className="list-group-item-action"
      >
        <div className="d-flex align-items-center flex-grow-1">
          <div className="me-auto">
            <div className="fw-medium" style={{ fontSize: "0.95rem" }}>
              {item.descricao}
            </div>
            {item.codigo && (
              <small className="text-muted" style={{ fontSize: "0.8rem" }}>
                Cód. {item.codigo}
              </small>
            )}
          </div>
          <div onClick={(e) => e.stopPropagation()} className="ms-2">
            <Form.Check
              type="checkbox"
              checked={selected}
              onChange={() => toggleItemSelection(item)}
              className="me-1"
              style={{ transform: "scale(1)" }}
              label=""
            />
          </div>
        </div>
      </div>
    );
  };

  // Limpa todos os itens selecionados
  const handleClearAllItems = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalSelectedItems(new Map());

    if (itemCountRef.current) {
      itemCountRef.current.innerText = "0 itens selecionados";
    }

    onConfirm([]);
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="offcanvas-selecao-itens"
      style={{ width: "550px", maxWidth: "100%" }}
    >
      <Offcanvas.Header className="border-bottom py-3">
        <div className="d-flex align-items-center w-100 justify-content-between">
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              className="me-2 p-1 text-body-secondary border-0"
              onClick={onHide}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
            <Offcanvas.Title className="ms-1 h4 mb-0 fw-bold">
              {aplicarPara === "PRODUTO"
                ? "Produtos"
                : aplicarPara === "DEPARTAMENTO"
                ? "Departamentos"
                : "Categorias"}
            </Offcanvas.Title>
          </div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="p-0 d-flex flex-column h-100">
        <div className="p-4 border-bottom bg-body">
          <p className="mb-3 small text-muted">
            Selecione os itens aos quais você quer aplicar esta promoção.
          </p>

          <InputGroup className="shadow-sm rounded overflow-hidden">
            <InputGroup.Text className="bg-white border-end-0 ps-3">
              <FontAwesomeIcon icon={faSearch} className="text-primary" />
            </InputGroup.Text>
            <Form.Control
              placeholder={`Pesquisar ${
                aplicarPara === "PRODUTO"
                  ? "produtos"
                  : aplicarPara === "DEPARTAMENTO"
                  ? "departamentos"
                  : "categorias"
              }...`}
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              className="border-start-0 py-2"
            />
          </InputGroup>

          {localSelectedItems.size > 0 && (
            <div className="mt-3">
              <Badge
                bg="light"
                text="dark"
                className="d-inline-flex align-items-center py-2 px-3 border shadow-sm bg-body"
                style={{ borderRadius: "50px" }}
              >
                <span ref={itemCountRef} className="text-body-secondary">
                  {localSelectedItems.size}{" "}
                  {localSelectedItems.size === 1
                    ? "item selecionado"
                    : "itens selecionados"}
                </span>
                <Button
                  variant="link"
                  className="p-0 ms-2 text-body-secondary border-0"
                  onClick={handleClearAllItems}
                  aria-label="Remover todos os itens"
                  style={{ fontSize: "14px" }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </Badge>
            </div>
          )}
        </div>

        <div className="overflow-auto flex-grow-1" ref={listContainerRef}>
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : allItems.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <FontAwesomeIcon
                  icon={faSearch}
                  size="3x"
                  className="text-muted"
                />
              </div>
              <h5>Nenhum item encontrado</h5>
              <p className="text-muted">
                {termoBusca
                  ? "Tente usar outro termo de busca"
                  : isInitialized
                  ? `Nenhum ${
                      aplicarPara === "PRODUTO"
                        ? "produto"
                        : aplicarPara === "DEPARTAMENTO"
                        ? "departamento"
                        : "categoria"
                    } encontrado`
                  : `Digite para pesquisar ${
                      aplicarPara === "PRODUTO"
                        ? "produtos"
                        : aplicarPara === "DEPARTAMENTO"
                        ? "departamentos"
                        : "categorias"
                    }`}
              </p>
            </div>
          ) : (
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={itemCount}
              loadMoreItems={loadMoreItems}
              threshold={5}
            >
              {({ onItemsRendered, ref }: any) => (
                <List
                  className="List"
                  height={600}
                  itemCount={itemCount}
                  itemSize={70}
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                  width="100%"                
                >
                  {ItemRow}
                </List>
              )}
            </InfiniteLoader>
          )}
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default SelecaoItensOffcanvas;
